import { useCallback, useContext, useMemo, useRef, useState } from "react";
import { toast, ToastOptions } from "react-toastify";
import ProjectService from "@app/projects/projectService";
import ZipProjectService from "@app/projects/zipProjectService";
import { BaseContext } from "@app/network-portfolio-management/contexts/BaseContext";
import { useNpmInvalidateQueries } from "@app/network-portfolio-management/services/npmQueries";
import { NetworkPortfolioManagementProjectActionStatus } from "@app/network-portfolio-management/interfaces";

const toastOptions: ToastOptions = { containerId: "z-index-9999" };

export interface UseBulkProjectActions {
  /** True while a bulk action is being submitted; disables the action button / prevents double-submit. */
  isDispatching: boolean;
  /** `comment` is forwarded to the publish endpoint (collected by the publish modal); ignored for run. */
  dispatch: (kind: NetworkPortfolioManagementProjectActionStatus, projectIds: number[], comment?: string) => Promise<void>;
}

const noun = (count: number): string => (count === 1 ? "project" : "projects");
const errorMessage = (kind: NetworkPortfolioManagementProjectActionStatus, count: number): string =>
  `There was a problem ${kind === NetworkPortfolioManagementProjectActionStatus.Run ? "running" : "publishing"} the selected ${noun(count)}. Please try again.`;

/**
 * Drives the NPM bulk Run / Publish actions by reusing the existing project endpoints
 * (projects/submit and projects/publish via {@link ProjectService}). Each outcome is announced with a toast.
 *
 * Run only *queues* the projects (jobs can take ~30 min), so we confirm they were queued and move on. Publish
 * completes synchronously (the response lists the published IDs, which may be a subset of those selected — any that
 * didn't publish are surfaced in a separate error toast).
 *
 * After either action succeeds we re-query the list against the current filter so the acted-on projects reflect
 * their new state immediately. Run is also covered by the notification-driven refresh (WI #194962 / PR !236194,
 * which subscribes to the per-project run-status SignalR events emitted by projects/submit), but forcing a refresh
 * here is safe and gives immediate feedback — it only re-evaluates the filtered cohort, never unselected projects.
 *
 * @param onAllComplete Invoked after a successful dispatch (used to clear the selection).
 */
// Split the selected ids into zip vs. custom against the zip lookup. The lookup is built from the full
// filtered cohort (BaseContext), so Select All partitions correctly even for unloaded pages — no extra call.
const partitionByType = (projectIds: number[], zipProjectIds: Set<number>): { customIds: number[]; zipIds: number[] } => {
  const zipIds: number[] = [];
  const customIds: number[] = [];
  for (const id of projectIds) {
    (zipProjectIds.has(id) ? zipIds : customIds).push(id);
  }
  return { customIds, zipIds };
};

// A rejected endpoint-group counts as zero for that group rather than failing the whole dispatch, so a
// partial success on the other endpoint is still reported and refreshed.
const settledCount = <T>(result: PromiseSettledResult<T>, toCount: (value: T) => number): number =>
  result.status === "fulfilled" ? toCount(result.value) : 0;

export function useBulkProjectActions(onAllComplete: () => void): UseBulkProjectActions {
  const projectService = useRef(new ProjectService()).current;
  const zipProjectService = useRef(new ZipProjectService()).current;
  const { zipProjectIds } = useContext(BaseContext);

  const [isDispatching, setIsDispatching] = useState(false);

  // Synchronous in-flight guard against double-submit (state alone would be a stale closure on rapid re-entry).
  const inFlight = useRef(false);
  const onAllCompleteRef = useRef(onAllComplete);
  onAllCompleteRef.current = onAllComplete;

  // Re-fetch every project-list view against the current filter criteria: the filtered IDs are re-evaluated
  // (acted-on projects drop out of the cohort), which cascades to the paged, grouped, and sorted views.
  const invalidateProjectLists = useNpmInvalidateQueries();

  const dispatch = useCallback(async (kind: NetworkPortfolioManagementProjectActionStatus, projectIds: number[], comment?: string): Promise<void> => {
    if (projectIds.length === 0 || inFlight.current) return;

    inFlight.current = true;
    setIsDispatching(true);

    // Zip and custom projects have separate Run/Publish endpoints; partition the selection, call both
    // services (each already no-ops for an empty group), and aggregate the outcomes into one toast.
    const { customIds, zipIds } = partitionByType(projectIds, zipProjectIds);

    try {
      switch (kind) {
        case NetworkPortfolioManagementProjectActionStatus.Publish: {
          // Each group is settled independently: a rejection from one endpoint must not suppress reporting or
          // refreshing the other's success. A rejected group counts its whole batch as not published, same as
          // an endpoint that ran but published fewer ids than requested.
          const [customResult, zipResult] = await Promise.allSettled([
            projectService.bulkPublish(customIds, comment ?? ""),
            zipProjectService.bulkPublishZipProjects(zipIds, comment ?? ""),
          ]);
          const publishedCount = settledCount(customResult, (r) => r.length) + settledCount(zipResult, (r) => r.length);
          if (publishedCount === 0) {
            toast.error(errorMessage(NetworkPortfolioManagementProjectActionStatus.Publish, projectIds.length), toastOptions);
            return;
          }
          const failedCount = projectIds.length - publishedCount;
          if (failedCount > 0) {
            toast.warn(`${publishedCount} ${noun(publishedCount)} published, ${failedCount} ${noun(failedCount)} could not be published. Please check those projects.`, toastOptions);
          } else {
            toast.success(`${publishedCount} ${noun(publishedCount)} — Publish complete.`, toastOptions);
          }
          break;
        }
        case NetworkPortfolioManagementProjectActionStatus.Run: {
          const [customResult, zipResult] = await Promise.allSettled([
            projectService.bulkRun(customIds),
            zipProjectService.bulkRunZipProjects(zipIds),
          ]);
          const queuedCount = settledCount(customResult, (r) => r.submitResults.length) + settledCount(zipResult, (r) => r.submitResults.length);
          if (queuedCount === 0) {
            toast.error(errorMessage(NetworkPortfolioManagementProjectActionStatus.Run, projectIds.length), toastOptions);
            return;
          }
          const failedCount = projectIds.length - queuedCount;
          if (failedCount > 0) {
            toast.warn(`${queuedCount} ${noun(queuedCount)} queued to run, ${failedCount} ${noun(failedCount)} could not be queued to run. Please check those projects.`, toastOptions);
          } else {
            toast.success(`${queuedCount} ${noun(queuedCount)} queued to run.`, toastOptions);
          }
          break;
        }
      }
      // Force a refresh of the acted-on projects against the current filter so they reflect their new state
      // immediately (drops published projects from the Ready to Publish cohort; surfaces run-status changes
      // without waiting on the notification path). Safe for both actions — see the hook doc above.
      void invalidateProjectLists();
      onAllCompleteRef.current();
    } catch {
      toast.error(errorMessage(kind, projectIds.length), toastOptions);
    } finally {
      inFlight.current = false;
      setIsDispatching(false);
    }
  }, [projectService, zipProjectService, invalidateProjectLists, zipProjectIds]);

  return useMemo(
    () => ({ dispatch, isDispatching }),
    [dispatch, isDispatching],
  );
}
